import { Fragment, useState } from 'react';
import styled from 'styled-components';

import { ErrorMessage } from '../../../../GlobalComponents/ErrorMessage';
import { Modal } from '../../../../GlobalComponents/Modal';
import { useAppDispatch, useAppSelector } from '../../../../reduxstore/hooks';
import { useRemoveCardMutation } from '../../../api';
import { setCard } from '../../../state/profileSlice';
import CardActions from '../components/CardActions';
import ConfirmationModal from '../components/ConfirmationModal';
import AddBalance from './AddBalance';
import BalanceError from './BalanceError';

const CardControls = ({
	cardNumber,
	source,
	balance,
	showRemoveCard,
	showAddBalance,
	setLoadingState,
	closeOnClick
}: CardControlsProps): JSX.Element => {
	const [showModal, setShowModal] = useState<boolean>(false);
	const [showError, setShowError] = useState<boolean>(false);
	const [showConfirmationDialog, setConfirmationDialog] =
		useState<boolean>(false);

	const [removeGiftCard, { isError, error, isLoading }] =
		useRemoveCardMutation();
	const dispatch = useAppDispatch();
	const userId = useAppSelector(state => state.authReducer.id);

	const toggleHandler = () => {
		setShowModal(!showModal);
	};
	const removeCardToggle = (): void => {
		setConfirmationDialog(!showConfirmationDialog);
	};
	const toggleErrorHandler = () => {
		setShowModal(false);
		setShowError(!showError);
	};

	const removeCard = (): void => {
		removeCardToggle();
		removeGiftCard({ id: userId, card_number: cardNumber })
			.unwrap()
			.then((response: ProfileResponse) => {
				dispatch(setCard(response.data.profile.cards));
				if (source && source === 'modal') {
					closeOnClick();
				}
			});
	};
	if (source && source !== 'modal') {
		setLoadingState(isLoading);
	}

	return (
		<Fragment>
			{isError && (
				<ErrorContainer>
					<ErrorMessage error={error}></ErrorMessage>
				</ErrorContainer>
			)}
			<CardActions
				toggleHandler={toggleHandler}
				removeCardHandler={removeCardToggle}
				isRemoveCardLoading={isLoading}
				showAddBalance={showAddBalance}
				showRemoveCard={showRemoveCard}
				disableRemoveCard={!!Number(balance)} // If there's a balance on the card, the "remove card" button should be inactive.
			/>
			{/* Card Action Model */}
			<Fragment>
				<Modal
					isOpen={showModal}
					toggleHandler={toggleHandler}
					size="lg"
				>
					<AddBalance
						onClose={() => setShowModal(false)}
						cardNumber={cardNumber}
					/>
				</Modal>

				<Modal
					isOpen={showError}
					toggleHandler={toggleErrorHandler}
					size="md"
				>
					<BalanceError
						toggleHandler={() => setShowError(false)}
						buttonText="GO BACK"
					/>
				</Modal>

				<Modal
					isOpen={showConfirmationDialog}
					toggleHandler={removeCardToggle}
					size="md"
				>
					<ConfirmationModal
						title="Are you sure?"
						message={
							<Fragment>
								Would you like to remove card # {cardNumber}{' '}
								from your account?
							</Fragment>
						}
						cancelHandler={removeCardToggle}
						confirmHandler={removeCard}
					/>
				</Modal>
			</Fragment>
		</Fragment>
	);
};

export interface CardControlsProps {
	cardNumber: string | number;
	showRemoveCard: boolean;
	showAddBalance: boolean;
	balance?: number;
	source?: 'dashboard' | 'modal' | 'scroll';
	setLoadingState: (isProcessing: boolean) => void;
	closeOnClick: () => void;
}

const ErrorContainer = styled.div`
	padding-top: 10px;
`;

export default CardControls;
